import React, { useEffect, useRef } from "react";
import { connect, ConnectedProps } from "react-redux";
import { General, Upgrade } from "../../../../lib/types";
import LevelTree from "./LevelTree";
import { icons } from "../../../../assets/images/icons";
import { investTechPointXP } from "../../../../store/actions";
import DialogButton from "../../../Common/DialogButton";
import { GENERAL_TYPE, UPGRADES } from "../../../../lib/enums";
import { setSameHeight } from "../../../../lib/elementUtilities";

interface IProps {
  general: General;
  typeName: string;
  typeId: number;
  techPoints: number;
}

const mapStateToProps = (state: any) => ({
  credits: state.governorate.player.credits,
  resetGeneral: state.governorate.upgrades
    .find((u: Upgrade) => u.id === UPGRADES.ResetGeneral),
  spells: state.governorate.spells,
  magicClosed: state.governorate.magicClosed
})

const mapToDispatchToProps = (dispatch: any) => ({
  investTechPointXPDispatch: (generalId: number) => dispatch(investTechPointXP(generalId))
})

const connector = connect(mapStateToProps, mapToDispatchToProps);

const GeneralDetail = ({
  general,
  typeName,
  typeId,
  techPoints,
  credits,
  resetGeneral,
  spells,
  magicClosed,
  investTechPointXPDispatch
}: IProps & ConnectedProps<typeof connector>) => {
  const divRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setSameHeight(divRef.current || undefined);
  }, [general]);

  const showPlanCancelInfo = () => typeId === GENERAL_TYPE.MAGE
    && ((magicClosed && general.skills.some(skill => skill.level && skill.tier === 2))
    || (spells.cast.some((s: any) => [52, 53, 54, 55].includes(s.id))));
  const showGlobeCancelInfo = () => typeId === GENERAL_TYPE.MAGE
    && spells.active.some((s: any) => s.spellId === 53);

  return (
    <section className="boxes-group general">
      <h2 className="h2 js-gen-name">
        <span className="bordered">{typeName}</span></h2>
      <hr className="divider"/>
      <div className="box text-center padding">
        <h2 className="h1 text-capitalize case-sensitive spacer">Level {general.level}, Skill
          points: {general.skillPoints}</h2>
        <p className="progress-bar">
      <span className="progress positive"
            style={{ width: `${general.nextLevelExperience !== 0 ? Math.floor(100 * general.experience / general.nextLevelExperience) : 0}%` }}/>
        </p>
        <p className="positive">
          Experience {general.experience} / {general.nextLevelExperience}
        </p>
      </div>
      <span className="divider"/>
      <div className="box">
        <div ref={divRef} className="row persons no-hover">
          {
            Array(4).fill("").map((_, index) => <LevelTree
              general={general}
              generalTypeId={typeId}
              tier={index + 1}
              strengthBonus={(general.strengthBonus.find(bonus => bonus.tier === index + 1)
                || { bonus: 0 }).bonus}
            />)
          }
        </div>
      </div>
      <span className="divider"/>
      <div className="box padding">
        <div className="row">
          <div className={"col-sm-6"}>
            <span className={"text-bigger"} style={{ paddingRight: "5px" }}>Reset skills</span>
            <DialogButton
              dialogOptions={{
                name: "PurchaseUpgrade",
                header: "Reset general's skills",
                id: UPGRADES.ResetGeneral,
                params: { gid: general.id },
                question: `Reset general's skills and strength bonuses?${showPlanCancelInfo()
                  ? "<br />Your magic plan is closed or you have planned ultimate general spells. "
                  + "All planned spells will be canceled and spent mana and magic energy returned."
                  : ""}${showGlobeCancelInfo()
                  ? "<br />Globe of invulnerability is cast on yourself. It will be dispersed." : ""}`,
                purchasable: !(credits < resetGeneral.price),
                price: resetGeneral.price
              }}
            >
              GET <img src={icons.credit} alt=""/>
            </DialogButton>
          </div>
          <div className="col-sm-6">
            <span className={"text-bigger"} style={{ paddingRight: "5px" }}>Exchange 1 tech. point for 100 XP</span>
            <a
              className={`btn btn-black ${techPoints ? "" : "disabled"}`}
              onClick={() => !!techPoints && investTechPointXPDispatch(general.id)}
            >
              Invest
            </a>
          </div>
        </div>
      </div>
      <span className="divider"/>
    </section>)
}

export default connector(GeneralDetail);
